/**
 * Order-Mirror (PIM P5) — FC-Bestellungen als Shopify-Umsatz spiegeln.
 *
 * Zweck: Shopify sieht den B2B-Umsatz (relevant u. a. für Shopify Capital).
 * Schutzmechanismen:
 *   • inventoryBehaviour: BYPASS — Shopify bucht KEINEN Bestand
 *     (Bestand läuft ausschließlich über die FC-Stock-Engine).
 *   • Tag 'fieldconnect-mirror' — der Order-Sync überspringt diese
 *     Bestellungen (Filter in shopifyClient), kein Rückimport, keine
 *     Doppelbuchung in Fibu.
 *   • Idempotent: orders.shopify_mirror_id verhindert Doppel-Spiegelung.
 *   • Fehler blockieren nie den FC-Ablauf (fire-and-forget + Log).
 */
import { base44 } from '@/api/base44Client';
import { shopifyGraphQL } from '@/lib/shopifyClient';
import { getOrderItems, getOrderTotal, isBestellung } from '@/lib/orderFields';

const CREATE = `
  mutation MirrorOrderCreate($order: OrderCreateOrderInput!, $options: OrderCreateOptionsInput) {
    orderCreate(order: $order, options: $options) {
      order { id name }
      userErrors { field message }
    }
  }
`;
const MARK_PAID = `
  mutation MirrorOrderPaid($input: OrderMarkAsPaidInput!) {
    orderMarkAsPaid(input: $input) {
      order { id displayFinancialStatus }
      userErrors { field message }
    }
  }
`;
const CANCEL = `
  mutation MirrorOrderCancel($orderId: ID!, $notifyCustomer: Boolean, $reason: OrderCancelReason!, $refund: Boolean!, $restock: Boolean!) {
    orderCancel(orderId: $orderId, notifyCustomer: $notifyCustomer, reason: $reason, refund: $refund, restock: $restock) {
      job { id }
      userErrors { field message }
    }
  }
`;

const isShopifyGid = (id) => String(id || '').startsWith('gid://');
// Netto-Stückpreis nach Rabatt (unit_price enthält nur die Erhöhung)
const netPrice = (it) => Number(it.discounted_unit_price ?? it.unit_price) || 0;

const eur = (v) => ({ shopMoney: { amount: String((Number(v) || 0).toFixed(2)), currencyCode: 'EUR' } });

/** Spiegelbar? Nur echte FC-Bestellungen (nie Shopify/eBay-Importe, nie Angebote) */
export function isMirrorable(order) {
  if (!order || !isBestellung(order)) return false;
  if (order.shopify_mirror_id) return false;
  if (['shopify', 'ebay'].includes((order.source || '').toLowerCase())) return false;
  if (order.status === 'Storniert') return false;
  return true;
}

/**
 * Bestellung in Shopify spiegeln. Gibt {ok, shopifyOrderId|message} zurück.
 * Bereits gespiegelte / nicht spiegelbare Bestellungen: no-op.
 */
export async function mirrorOrderToShopify(order) {
  if (!isMirrorable(order)) return { ok: false, message: 'nicht spiegelbar / bereits gespiegelt' };

  const items = getOrderItems(order);
  if (items.length === 0) return { ok: false, message: 'keine Positionen' };

  const lineItems = items.map((it) => ({
    title: it.product_name || it.name || 'Position',
    sku: it.product_sku || it.sku || undefined,
    quantity: Math.max(1, Math.round(Number(it.quantity) || 1)),
    priceSet: eur(netPrice(it)),
    requiresShipping: false,
    taxLines: [{
      title: 'MwSt',
      rate: (Number(order.tax_rate) || 19) / 100,
      priceSet: eur((Number(it.quantity) || 1) * netPrice(it) * ((Number(order.tax_rate) || 19) / 100)),
    }],
  }));

  const bezahlt = !!order.paid_at;
  const total = getOrderTotal(order);

  const input = {
    // Hinweis im Shopify-Admin, Herkunft eindeutig erkennbar
    tags: ['fieldconnect-mirror', order.order_number].filter(Boolean),
    note: `FieldConnect B2B-Bestellung ${order.order_number || ''} — Agent: ${order.agent_email || '—'}`,
    email: order.lead_email || undefined,
    lineItems,
    // bezahlt → sofort als bezahlte Transaktion (Capital wertet Umsatz)
    ...(bezahlt && total > 0 && {
      transactions: [{ kind: 'SALE', status: 'SUCCESS', amountSet: eur(total) }],
    }),
  };

  const res = await shopifyGraphQL(CREATE, {
    order: input,
    options: { inventoryBehaviour: 'BYPASS', sendReceipt: false, sendFulfillmentReceipt: false },
  });
  const errs = res?.orderCreate?.userErrors || [];
  if (errs.length > 0) return { ok: false, message: errs.map((e) => e.message).join('; ') };
  const created = res?.orderCreate?.order;
  if (!created?.id) return { ok: false, message: 'Shopify hat keine Bestellung zurückgegeben' };

  await base44.entities.Order.update(order.id, {
    shopify_mirror_id: created.id,
    mirror_synced_at: new Date().toISOString(),
  });
  return { ok: true, shopifyOrderId: created.id, name: created.name };
}

/** Zahlung nachziehen: FC "Abgeschlossen/bezahlt" → Spiegel als paid markieren */
export async function markMirrorPaid(order) {
  if (order?.shopify_mirror_id && !isShopifyGid(order.shopify_mirror_id)) {
    return { ok: false, message: 'Shopify-Übertragung läuft noch' };
  }
  if (!order?.shopify_mirror_id) {
    // Noch kein Spiegel — jetzt (als bezahlt) spiegeln
    return mirrorOrderToShopify({ ...order, paid_at: order.paid_at || new Date().toISOString() });
  }
  const res = await shopifyGraphQL(MARK_PAID, { input: { id: order.shopify_mirror_id } });
  const errs = res?.orderMarkAsPaid?.userErrors || [];
  if (errs.length > 0) return { ok: false, message: errs.map((e) => e.message).join('; ') };
  return { ok: true };
}

/** FC-Storno → Spiegel in Shopify stornieren (kein Refund, kein Restock) */
export async function cancelMirror(order) {
  if (!isShopifyGid(order?.shopify_mirror_id)) return { ok: false, message: 'kein Spiegel vorhanden' };
  const res = await shopifyGraphQL(CANCEL, {
    orderId: order.shopify_mirror_id,
    notifyCustomer: false,
    reason: 'OTHER',
    refund: false,
    restock: false,
  });
  const errs = res?.orderCancel?.userErrors || [];
  if (errs.length > 0) return { ok: false, message: errs.map((e) => e.message).join('; ') };
  return { ok: true };
}

/** Fire-and-forget Wrapper — FC-Abläufe dürfen nie am Mirror scheitern */
export function mirrorInBackground(fn, label) {
  Promise.resolve()
    .then(fn)
    .then((r) => {
      if (r && !r.ok && r.message !== 'nicht spiegelbar / bereits gespiegelt') {
        console.warn(`[orderMirror] ${label}:`, r.message);
      }
    })
    .catch((err) => console.warn(`[orderMirror] ${label}:`, err.message));
}
